
// Which of a feature's own vertices are offered as snap targets for
// subsequent vertex placement (see DigitizationModule.registerFeatureSnapTargets
// / findSnapTarget / _pickPlacementPosition). Pure - a plain GeoJSON-shaped
// Feature in, an array of [x,y,z] out - so it is Level-1 unit testable.
//
// Audit finding (geometry-core audit): this used to be "endpoints only" for a
// LineString (coordinates[0] and coordinates[n-1]). That silently made every
// vertex a topology operation INSERTS into a line - the crossing node from
// "Trim mod linje" / "Sæt punkt ved skæring" / "Indsæt punkt" - unreachable
// by capture-time snapping, even though those operations exist precisely so
// that node can be built on afterwards, and even though `_findHoveredVertex`
// (the vertex-context-menu hit test) already lets the user target ANY vertex.
// Every real vertex of a LineString / Polygon ring is now a snap target, so
// the two "what can I snap to" mechanisms agree.

function toXYZ(coord){
	return [coord[0], coord[1], coord.length > 2 ? coord[2] : 0];
}

export function snapTargetPointsForFeature(feature){
	if(!feature || !feature.geometry) return [];
	const {type, coordinates} = feature.geometry;

	if(type === "Point"){
		return Array.isArray(coordinates) && coordinates.length >= 2 ? [toXYZ(coordinates)] : [];
	}
	if(type === "LineString"){
		return Array.isArray(coordinates) ? coordinates.map(toXYZ) : [];
	}
	if(type === "Polygon"){
		// outer ring only (this codebase has no hole support anywhere); the
		// stored ring repeats its first vertex as the last to close it -
		// FeatureVisual.toCoordinates() - so drop that duplicate to avoid two
		// identical snap targets at the seam.
		const ring = Array.isArray(coordinates) ? coordinates[0] : null;
		if(!Array.isArray(ring) || ring.length === 0) return [];
		const n = ring.length;
		const closed = n > 1
			&& ring[0][0] === ring[n - 1][0]
			&& ring[0][1] === ring[n - 1][1]
			&& (ring[0][2] || 0) === (ring[n - 1][2] || 0);
		return (closed ? ring.slice(0, -1) : ring).map(toXYZ);
	}
	return [];
}

// ── Snap-indstillinger (ren logik, Level-1-testbar) ────────────────────────
export const DEFAULT_SNAP_TOLERANCE_PX = 15;
export const MIN_SNAP_TOLERANCE_PX = 3;
export const MAX_SNAP_TOLERANCE_PX = 50;

// Brugerens (muligvis tomme/ugyldige/ekstreme) Snap-afstand -> en brugbar
// pixelvaerdi. 0 betyder ikke "snap slaaet fra" (det er snap.enabled) men
// "ikke gyldigt tal": et tomt/0-felt i indstillingsdialogen (se
// digitization-settings.vue onNumber) maa aldrig give en tolerance paa 0 px,
// som aldrig kan ramme noget, og heller ikke en tolerance paa 5000 px, der
// snapper til alt.
export function normalizeSnapTolerancePx(raw){
	const n = typeof raw === "number" ? raw : parseFloat(raw);
	if(!isFinite(n) || n <= 0) return DEFAULT_SNAP_TOLERANCE_PX;
	return Math.min(MAX_SNAP_TOLERANCE_PX, Math.max(MIN_SNAP_TOLERANCE_PX, n));
}

// Naermeste snap-kandidat inden for `maxPixels` skaermpixels. `items`:
// [{x, y, position}] allerede projiceret til canvas-pixels af kalderen (som
// ogsaa har filtreret usynlige registreringer fra). Samme maal som
// findSnapTarget altid har brugt: euklidisk skaermafstand, naermeste vinder.
export function nearestSnapCandidate(items, mouse, maxPixels){
	let best = null;
	for(const item of items){
		const distPx = Math.hypot(item.x - mouse.x, item.y - mouse.y);
		if(distPx <= maxPixels && (!best || distPx < best.distPx)){
			best = {position: item.position, distPx};
		}
	}
	return best;
}
